import {
  faChevronLeft,
  faChevronRight,
  faCompressAlt,
  faExpandAlt,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Portal } from '@headlessui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShortAddress } from '~/components/ui/Address';
import { BuildingPlaceholder } from '~/components/ui/BuildingPlaceholder';
import { Image } from '~/components/ui/Image';
import { initials } from '~/helpers/initials';
import { useBreakpoint } from '~/hooks/useBreakpoint';

// Types
interface Site {
  id: string;
  name: string;
  image?: string | null;
  address?: string | null;
}

interface CalendarSidebarProps {
  sites: Site[];
  currentSiteId?: string | null;
  isSidebarExpanded: boolean;
  setIsSidebarExpanded: (expanded: boolean) => void;
  isManuallyExpanded: boolean;
  setIsManuallyExpanded: (manual: boolean) => void;
  expandedSiteIds: string[];
  onToggleSiteExpansion: (siteId: string) => void;
  onSidebarMouseEnter: () => void;
  onSidebarMouseLeave: () => void;
  siteHeight: (siteId: string) => number;
  navigationPath?: string; // Custom navigation path, defaults to '/calendar/monthly'
  viewType?: 'calendar' | 'planner'; // Determines whether to show dimming and row height
  variant?: 'schedule' | 'calendar'; // Determines positioning behavior on mobile
  onSiteNavigate?: (site: Site, isCurrent: boolean) => void; // Optional explicit navigation handler
  focusSiteId?: string; // Optional target for auto-scroll using tanstack virtualizer
  focusRequestToken?: number; // Monotonic token to force reapply even for same site
}

const sanitizeName = (name?: string) =>
  (
    initials(name || '')
      .toUpperCase()
      .match(/[A-Z0-9]/g) || []
  ).join('');

const computeInitialParts = (name?: string) => {
  const normalized = (name || '').trim();
  if (!normalized) return { base: '??', third: '?' };

  const digitsMatch = normalized.match(/^\s*([0-9]{1,2})/);
  const digits = digitsMatch?.[1] || '';
  const remainder = normalized.slice(
    digitsMatch ? normalized.indexOf(digits) + digits.length : 0
  );
  const firstAlphaMatch = remainder.match(/[A-Za-z]/);

  if (digits && firstAlphaMatch) {
    const base = `${digits}${firstAlphaMatch[0].toUpperCase()}`;
    const rest = remainder.slice(remainder.indexOf(firstAlphaMatch[0]) + 1);
    const next =
      rest.match(/[A-Za-z0-9]/)?.[0]?.toUpperCase() ||
      firstAlphaMatch[0].toUpperCase() ||
      '?';
    return { base, third: next };
  }

  const cleaned = sanitizeName(normalized);
  if (!cleaned) return { base: '??', third: '?' };
  const base =
    cleaned.length === 1 ? `${cleaned}${cleaned}` : cleaned.slice(0, 2);
  const third = cleaned[2] ?? cleaned[1] ?? cleaned[0] ?? '?';
  return { base, third };
};

const sidebarWrapperVariants = cva(
  'z-[19] flex flex-col border-r border-grey-20 bg-white',
  {
    variants: {
      mobileCalendar: {
        true: 'absolute left-0 top-0 h-full',
        false: 'sticky left-0 min-h-full',
      },
      isExpanded: {
        true: 'w-[333px]',
        false: 'w-[83px]',
      },
      isMobile: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        isMobile: true,
        isExpanded: true,
        class: 'w-[333px]',
      },
      {
        isMobile: true,
        isExpanded: false,
        class: 'w-[66px]',
      },
    ],
    defaultVariants: {
      mobileCalendar: false,
      isExpanded: true,
      isMobile: false,
    },
  }
);

const sidebarHeaderVariants = cva(
  'sticky top-0 z-10 flex h-14 w-full items-center gap-3 border-b border-outline-variant bg-white',
  {
    variants: {
      isExpanded: {
        true: 'justify-between px-4 lg:px-[26px]',
        false: 'justify-center',
      },
    },
    defaultVariants: {
      isExpanded: true,
    },
  }
);

const desktopCardVariants = cva(
  'group relative flex cursor-pointer items-center border-b transition-opacity duration-200',
  {
    variants: {
      contextVariant: {
        calendar: 'border-bright mx-3 hover:rounded-md hover:bg-shade-10',
        schedule: 'border-outline-variant',
      },
      isCurrent: {
        true: '',
        false: '',
      },
      isExpanded: {
        true: 'justify-start px-3 lg:pr-4 lg:pl-3',
        false: 'justify-center',
      },
      dimmed: {
        true: 'opacity-90 hover:opacity-75',
        false: '',
      },
    },
    compoundVariants: [
      {
        contextVariant: 'calendar',
        isCurrent: true,
        class: 'rounded-md border-b-0 !text-white',
      },
      {
        contextVariant: 'calendar',
        isCurrent: true,
        isExpanded: true,
        class: 'hover:bg-shade-25 bg-shade-25',
      },
      {
        contextVariant: 'calendar',
        isCurrent: true,
        isExpanded: false,
        class: 'bg-brand',
      },
    ],
    defaultVariants: {
      contextVariant: 'calendar',
      isCurrent: false,
      isExpanded: true,
      dimmed: false,
    },
  }
);

const desktopInnerVariants = cva('flex items-center overflow-hidden', {
  variants: {
    isExpanded: {
      true: 'w-full gap-3',
      false: 'gap-0',
    },
  },
  defaultVariants: {
    isExpanded: true,
  },
});

const mobileCardVariants = cva(
  'group relative flex cursor-pointer items-center border-b px-3 lg:px-6',
  {
    variants: {
      contextVariant: {
        calendar: 'border-bright',
        schedule: 'border-outline-variant',
      },
      isExpanded: {
        true: 'justify-start',
        false: 'justify-center',
      },
      dimmed: {
        true: 'opacity-50',
        false: '',
      },
      isCurrent: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        contextVariant: 'calendar',
        isExpanded: true,
        isCurrent: true,
        class: 'bg-shade-25',
      },
      {
        contextVariant: 'calendar',
        isExpanded: false,
        isCurrent: true,
        class: 'bg-brand',
      },
    ],
    defaultVariants: {
      contextVariant: 'calendar',
      isExpanded: true,
      dimmed: false,
      isCurrent: false,
    },
  }
);

const collapsedInitialsVariants = cva(
  'flex h-full w-full items-center justify-center rounded-md text-sm font-semibold uppercase text-brand',
  {
    variants: {
      isCurrent: {
        true: 'bg-white/70',
        false: 'bg-shade-10',
      },
    },
    defaultVariants: {
      isCurrent: false,
    },
  }
);

interface SiteCardProps {
  site: Site;
  height: number;
  isSidebarExpanded: boolean;
  expandedRow: boolean;
  onToggleExpandedRow: (siteId: string) => void;
  isCurrentSite?: boolean;
  dimmed?: boolean;
  navigationPath: string;
  // PlaceCard-specific props
  dataUsetiful?: string;
  virtualRef?: (node: Element | null) => void;
  siteIndex?: number;
  linkPrefix?: string;
  headerHeight?: number;
  siteTopFn?: (siteId: string) => number;
  expandSite?: (siteId: string) => void;
  collapseSite?: (siteId: string) => void;
  variant?: 'sidebar' | 'schedule'; // Determines behavior
  currentSiteId?: string | null; // Added to disambiguate navigation on mobile
  contextVariant?: 'calendar' | 'schedule'; // New: explicit parent context
  onSiteNavigate?: (site: Site, isCurrent: boolean) => void; // Optional explicit navigation handler
  collapsedInitials: string; // Precomputed initials to ensure uniqueness
}

// Site Card component with mobile and desktop variants
function SiteCard({
  site,
  height,
  isSidebarExpanded,
  expandedRow,
  onToggleExpandedRow,
  isCurrentSite = false,
  dimmed = false,
  virtualRef,
  siteIndex,
  linkPrefix = '',
  variant = 'sidebar',
  contextVariant = 'calendar',
  onSiteNavigate,
  collapsedInitials,
}: SiteCardProps) {
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();

  // Mobile modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardPosition, setCardPosition] = useState({ x: 0, y: 0 });
  const [, setHasPointerLeft] = useState(false);

  // Long-press handling (mobile)
  const longPressTimeoutRef = useRef<number | null>(null);
  const longPressActivatedRef = useRef(false);
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const hasTouchMovedRef = useRef(false);
  const LONG_PRESS_DURATION_MS = 450;
  const MOVE_TOLERANCE_PX = 8;

  // Unified navigation for both desktop and mobile
  const handleNavigateForSite = useCallback(
    (targetSite: Site) => {
      // Allow parent to handle navigation explicitly when provided
      if (onSiteNavigate) {
        onSiteNavigate(targetSite, isCurrentSite);
        return;
      }

      if (isMobile) {
        // Mobile sidebar taps should not navigate away to planner/calendar
        return;
      }
      if (contextVariant === 'calendar') {
        if (isCurrentSite) {
          navigate('/jobs/planner', { state: { targetSiteId: targetSite.id } });
          return;
        }
        // Pass fromCalendar flag to prevent sidebar auto-scroll (user clicked the site)
        navigate(`/jobs/calendar/${encodeURIComponent(targetSite.id)}`, {
          state: { fromCalendar: true },
        });
        return;
      }
      // schedule context: always go to that site's monthly view
      // URL will drive the sidebar scroll in CalendarView
      navigate(`/jobs/calendar/${encodeURIComponent(targetSite.id)}`);
    },
    [navigate, contextVariant, isCurrentSite, isMobile, onSiteNavigate]
  );

  const handleExpandToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleExpandedRow(site.id);
  };

  useEffect(() => {
    if (!isCurrentSite) return;
    setHasPointerLeft(false);
  }, [isCurrentSite]);

  // Mobile modal effects
  useEffect(() => {
    if (!isMobile) return;

    let resizeObserver: ResizeObserver | null = null;

    if (isModalOpen && cardRef.current) {
      const updateCardPosition = () => {
        if (cardRef.current) {
          const rect = cardRef.current.getBoundingClientRect();
          setCardPosition({ x: rect.left, y: rect.top });
        }
      };

      updateCardPosition();
      resizeObserver = new ResizeObserver(updateCardPosition);
      resizeObserver.observe(cardRef.current);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isModalOpen, variant, isMobile]);

  useEffect(() => {
    if (!isMobile || !isModalOpen) return;

    const handleResize = () => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        setCardPosition({ x: rect.left, y: rect.top });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isModalOpen, variant, isMobile]);

  useEffect(() => {
    if (!isMobile || !isModalOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        cardRef.current &&
        !cardRef.current.contains(event.target as Node)
      ) {
        setIsModalOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setCardPosition({ x: rect.left, y: rect.top });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModalOpen, variant, isMobile]);

  // Long press lifecycle handlers (mobile)
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    if (longPressTimeoutRef.current) {
      window.clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
    longPressActivatedRef.current = false;
    hasTouchMovedRef.current = false;
    const t = e.touches[0];
    touchStartPosRef.current = { x: t.clientX, y: t.clientY };

    // Only enable long-press modal when the sidebar is NOT expanded
    if (!isSidebarExpanded) {
      longPressTimeoutRef.current = window.setTimeout(() => {
        // Activate long press
        longPressActivatedRef.current = true;
        if (cardRef.current) {
          const rect = cardRef.current.getBoundingClientRect();
          setCardPosition({ x: rect.left, y: rect.top });
        }
        setIsModalOpen(true);
      }, LONG_PRESS_DURATION_MS);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile) return;
    const start = touchStartPosRef.current;
    const t = e.touches[0];
    if (!start || !t) return;
    const dx = Math.abs(t.clientX - start.x);
    const dy = Math.abs(t.clientY - start.y);
    if (dx > MOVE_TOLERANCE_PX || dy > MOVE_TOLERANCE_PX) {
      if (longPressTimeoutRef.current) {
        window.clearTimeout(longPressTimeoutRef.current);
        longPressTimeoutRef.current = null;
      }
      // Mark that this interaction became a scroll/drag so we don't treat it as a tap
      hasTouchMovedRef.current = true;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isMobile) return;
    if (longPressTimeoutRef.current) {
      window.clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
    // If the user moved beyond tolerance, treat as scroll and do not navigate
    if (hasTouchMovedRef.current) {
      hasTouchMovedRef.current = false;
      return;
    }
    // If long press already activated, suppress tap navigation
    if (longPressActivatedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      // reset activation for next interaction
      setTimeout(() => {
        longPressActivatedRef.current = false;
      }, 0);
      return;
    }
    // Treat as regular tap -> navigate
    e.preventDefault();
    e.stopPropagation();
    handleNavigateForSite(site);
  };

  // Pre-render the image with proper sizing based on variant and device
  const placeholderClassName = classNames(
    variant === 'schedule' && isMobile ? 'text-base' : 'text-lg',
    contextVariant === 'calendar' && isCurrentSite && '!bg-shade-25 !text-brand'
  );

  const siteImage = site.image ? (
    <div
      className='h-9 w-9 lg:h-8 lg:w-8'
      style={{ imageRendering: 'crisp-edges' }}
    >
      <Image url={site.image} size='fit' />
    </div>
  ) : (
    <div
      className={classNames(
        'h-9 w-9 lg:h-8 lg:w-8',
        'flex items-center justify-center'
      )}
    >
      {isSidebarExpanded ? (
        <BuildingPlaceholder className={placeholderClassName} />
      ) : (
        <div
          className={collapsedInitialsVariants({
            isCurrent: isCurrentSite,
          })}
        >
          {collapsedInitials}
        </div>
      )}
    </div>
  );

  // Desktop variant (both sidebar and schedule)
  const renderDesktop = () => (
    <div
      className={desktopCardVariants({
        contextVariant,
        isCurrent: isCurrentSite,
        isExpanded: isSidebarExpanded,
        dimmed,
      })}
      style={{
        height: height,
      }}
      data-siteid={site.id}
      data-index={siteIndex}
      ref={virtualRef}
      onMouseLeave={() => {
        if (!isCurrentSite) return;
        setHasPointerLeft(true);
      }}
      onClick={() => handleNavigateForSite(site)}
    >
      <div className={desktopInnerVariants({ isExpanded: isSidebarExpanded })}>
        {siteImage}
        {isSidebarExpanded && (
          <div className='flex flex-1 flex-col overflow-hidden'>
            <Link
              to={`${linkPrefix}sites/${site.id}`}
              className={classNames(
                'w-fit cursor-pointer truncate py-1 text-sm font-medium leading-3 transition-colors hover:underline',
                isCurrentSite ? 'text-primary' : 'text-grey-50',
                'group-hover:text-primary',
                'hover:text-brand'
              )}
              onClick={(e) => e.stopPropagation()}
            >
              {site.name}
            </Link>
            {/* Show address when sidebar is expanded for both variants */}
            {site.address && (
              <div className='truncate py-0.5 text-xs font-light leading-none text-grey-50'>
                <ShortAddress data={site.address} />
              </div>
            )}
          </div>
        )}
      </div>
      {isSidebarExpanded && variant === 'schedule' && (
        <div
          className={
            'absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-sm opacity-0 ring-1 ring-black ring-opacity-0 transition-opacity duration-300 hover:cursor-pointer hover:ring-opacity-30 group-hover:opacity-100'
          }
          style={{ zIndex: 5, touchAction: 'manipulation' }}
          onClick={handleExpandToggle}
        >
          <FontAwesomeIcon
            icon={expandedRow ? faCompressAlt : faExpandAlt}
            size='xs'
            color='#80858E'
          />
        </div>
      )}
    </div>
  );

  // Mobile variant (both schedule and calendar)
  const renderMobile = () => (
    <>
      <div
        className={mobileCardVariants({
          contextVariant,
          isExpanded: isSidebarExpanded,
          dimmed,
          isCurrent: isCurrentSite,
        })}
        style={{
          height,
        }}
        data-siteid={site.id}
        data-index={siteIndex}
        ref={virtualRef}
        onClick={(e) => {
          // No-op on mobile; navigation handled by touch events to avoid double-trigger
          e.preventDefault();
          e.stopPropagation();
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className={classNames(
            isSidebarExpanded && 'flex-1 gap-2 ',
            'flex w-full items-center overflow-hidden'
          )}
        >
          <div
            ref={cardRef}
            className='relative h-9 w-9 lg:h-8 lg:w-8'
            style={{ touchAction: 'manipulation' }}
          >
            <div className='h-full w-full'>{siteImage}</div>
          </div>
          {isSidebarExpanded && (
            <div className='flex flex-1 flex-col overflow-hidden'>
              <Link
                to={`${linkPrefix}sites/${site.id}`}
                className={classNames(
                  'flex w-fit max-w-full cursor-pointer truncate py-0.5 text-xs font-medium transition-colors hover:underline lg:text-sm lg:leading-3',
                  isCurrentSite ? 'text-white' : 'text-grey-50',
                  'group-hover:text-primary',
                  'hover:text-brand'
                )}
                onClick={(e) => {
                  // Allow navigation to the side subroute like desktop; prevent parent handlers
                  e.stopPropagation();
                }}
                onTouchStart={(e) => {
                  // Prevent triggering parent long-press/tap handlers
                  e.stopPropagation();
                }}
                onTouchMove={(e) => {
                  e.stopPropagation();
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                }}
                title={`View site details for ${site.name}`}
              >
                {site.name}
              </Link>
              {/* Show address when sidebar is expanded for both variants */}
              {site.address && (
                <div className='truncate py-0.5 text-xs font-light leading-none text-grey-50'>
                  <ShortAddress data={site.address} />
                </div>
              )}
            </div>
          )}
        </div>
        {isSidebarExpanded && variant === 'schedule' && (
          <div
            className='transition-color flex h-6 w-6 flex-none cursor-pointer items-center justify-center active:bg-black active:bg-opacity-5'
            style={{ touchAction: 'manipulation', minWidth: '24px' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleExpandToggle(e);
              setIsModalOpen(false);
            }}
          >
            <FontAwesomeIcon
              icon={expandedRow ? faCompressAlt : faExpandAlt}
              size='xs'
              color='#80858E'
            />
          </div>
        )}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <Portal>
            <motion.div
              ref={modalRef}
              initial={{ opacity: 0, width: 28, height: 28, x: 0 }}
              animate={{
                opacity: 1,
                width: 320,
                height: height + 1,
                x: -cardPosition.x + 12,
              }}
              exit={{
                opacity: 0,
                width: 28,
                height: 28,
                x: 0,
                transition: {
                  duration: 0.2,
                  ease: [0.4, 0.0, 1, 1],
                },
              }}
              transition={{
                duration: 0.25,
                ease: [0.05, 0.7, 0.1, 1.0],
              }}
              className='fixed z-20 overflow-visible rounded-br-md rounded-tr-md border border-l-0 border-outline-variant bg-surface-container-low'
              style={{
                willChange: 'transform, width, height, opacity',
                transformOrigin: 'left center',
                maxWidth: '95vw',
                top: cardPosition.y - (height - 32) / 2,
                left: 0,
                pointerEvents: 'auto',
                backfaceVisibility: 'hidden',
                textRendering: 'geometricPrecision',
                WebkitFontSmoothing: 'subpixel-antialiased',
                MozOsxFontSmoothing: 'grayscale',
                touchAction: 'manipulation',
              }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  delay: 0.22,
                  duration: 0.15,
                }}
                className='absolute z-30 h-9 w-9 cursor-pointer'
                style={{
                  left: '0.9rem',
                  top: `${height / 2 - 17}px`,
                  touchAction: 'manipulation',
                }}
                onClick={() => setIsModalOpen(false)}
              >
                {siteImage}
              </motion.div>

              <motion.div
                className='ml-14 flex h-full items-center overflow-hidden truncate py-2 pr-5'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.15 } }}
                transition={{
                  duration: 0.15,
                  ease: [0.0, 0.0, 0.2, 1.0],
                  delay: 0.15,
                }}
                style={{
                  willChange: 'opacity',
                  textRendering: 'geometricPrecision',
                  WebkitFontSmoothing: 'subpixel-antialiased',
                  MozOsxFontSmoothing: 'grayscale',
                }}
              >
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10, transition: { duration: 0.1 } }}
                  transition={{ delay: 0.25 }}
                  className='flex min-w-0 flex-1 items-center justify-between gap-4'
                >
                  <div className='over flex w-full flex-col overflow-hidden'>
                    <h3
                      className='hover:text-blue-600 cursor-pointer truncate text-sm font-medium text-grey-50 transition-colors'
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleNavigateForSite(site);
                        setIsModalOpen(false);
                      }}
                      title={`View calendar for ${site.name}`}
                    >
                      {site.name}
                    </h3>
                    {site.address && (
                      <div
                        className='-mt-1 cursor-pointer truncate text-xs text-grey-50 hover:text-brand'
                        onClick={() => {
                          handleNavigateForSite(site);
                          setIsModalOpen(false);
                        }}
                      >
                        <ShortAddress data={site.address} />
                      </div>
                    )}
                  </div>
                  <div
                    className='transition-color flex h-6 w-6 flex-none cursor-pointer items-center justify-center active:bg-black active:bg-opacity-5'
                    style={{ touchAction: 'manipulation', minWidth: '24px' }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleExpandToggle(e);
                      setIsModalOpen(false);
                    }}
                  >
                    <FontAwesomeIcon
                      icon={expandedRow ? faCompressAlt : faExpandAlt}
                      size='xs'
                      color='#80858E'
                    />
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </Portal>
        )}
      </AnimatePresence>
    </>
  );

  if (isMobile) {
    return renderMobile();
  } else {
    return renderDesktop();
  }
}

/**
 * CalendarSidebar - Site list sidebar for calendar/planner views.
 * Memoized to prevent re-renders when parent updates unrelated state.
 * Uses internal virtualization for performance with large site lists.
 */
export const CalendarSidebar = React.memo(function CalendarSidebar({
  sites,
  currentSiteId,
  isSidebarExpanded,
  setIsSidebarExpanded,
  setIsManuallyExpanded,
  expandedSiteIds,
  onToggleSiteExpansion,
  onSidebarMouseEnter,
  onSidebarMouseLeave,
  siteHeight,
  navigationPath = '/jobs/calendar',
  viewType = 'calendar',
  variant = 'calendar',
  onSiteNavigate,
  focusSiteId,
  focusRequestToken,
}: CalendarSidebarProps) {
  const { isMobile } = useBreakpoint();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const focusAppliedRef = useRef<string | null>(null);
  const focusTokenAppliedRef = useRef<number | null>(null);

  // Sidebar size constants
  const sideBarWidth = 333;
  const collapsedSideBarWidth = 83;
  const mobileSideBarWidth = 333;
  const collapsedMobileSideBarWidth = 66;

  const actualSideBarWidth = React.useMemo(() => {
    return isMobile
      ? isSidebarExpanded
        ? mobileSideBarWidth
        : collapsedMobileSideBarWidth
      : isSidebarExpanded
      ? sideBarWidth
      : collapsedSideBarWidth;
  }, [isMobile, isSidebarExpanded]);

  // Lightweight virtualizer used for precise scroll positioning when focusing a site
  const sidebarVirtualizer = useVirtualizer({
    count: sites.length,
    getScrollElement: () => listRef.current,
    estimateSize: (index) => {
      const site = sites[index];
      if (!site) return viewType === 'calendar' ? 70 : 60;
      return viewType === 'calendar' ? 70 : siteHeight(site.id);
    },
    getItemKey: (index) => sites[index]?.id ?? index,
    overscan: 3,
  });

  const expandedSiteIdsKey = useMemo(
    () => expandedSiteIds.join('|'),
    [expandedSiteIds]
  );

  // Keep virtualizer measurements in sync with size-affecting changes
  useEffect(() => {
    sidebarVirtualizer.measure();
  }, [
    sidebarVirtualizer,
    sites.length,
    expandedSiteIdsKey,
    isSidebarExpanded,
    viewType,
    siteHeight,
  ]);

  // Auto-scroll to a requested site when provided by navigation state
  useEffect(() => {
    if (!focusSiteId) return;
    const alreadyApplied =
      focusAppliedRef.current === focusSiteId &&
      focusTokenAppliedRef.current === focusRequestToken;
    if (alreadyApplied) return;
    const targetIndex = sites.findIndex(({ id }) => id === focusSiteId);
    if (targetIndex < 0) return;

    const rafId = requestAnimationFrame(() => {
      sidebarVirtualizer.scrollToIndex(targetIndex, { align: 'start' });
      focusAppliedRef.current = focusSiteId;
      focusTokenAppliedRef.current = focusRequestToken ?? null;
    });

    return () => cancelAnimationFrame(rafId);
  }, [focusSiteId, focusRequestToken, sites, sidebarVirtualizer]);

  const handleSidebarToggle = () => {
    setIsSidebarExpanded(!isSidebarExpanded);
    setIsManuallyExpanded(!isSidebarExpanded);
  };

  const isSiteExpanded = useCallback(
    (siteId: string) => expandedSiteIds.includes(siteId),
    [expandedSiteIds]
  );

  const initialsBySiteId = useMemo(() => {
    const baseData = sites.map((site) => {
      const { base, third } = computeInitialParts(site.name);
      return { id: site.id, base, third };
    });

    const counts = baseData.reduce<Record<string, number>>((acc, { base }) => {
      acc[base] = (acc[base] || 0) + 1;
      return acc;
    }, {});

    return baseData.reduce<Record<string, string>>(
      (acc, { id, base, third }) => {
        const display = counts[base] > 1 ? `${base}${third}` : base;
        acc[id] = display;
        return acc;
      },
      {}
    );
  }, [sites]);

  const virtualItems = sidebarVirtualizer.getVirtualItems();

  return (
    <AnimatePresence mode='sync'>
      <motion.div
        ref={sidebarRef}
        className={sidebarWrapperVariants({
          mobileCalendar: isMobile && variant === 'calendar',
          isExpanded: isSidebarExpanded,
          isMobile,
        })}
        initial={false}
        animate={{
          width: actualSideBarWidth,
        }}
        transition={{
          duration: 0.3,
          ease: [0.4, 0.0, 0.2, 1],
        }}
        onTouchStart={(e) => e.stopPropagation()}
      >
        {/* Sidebar Header */}
        <div
          className={sidebarHeaderVariants({ isExpanded: isSidebarExpanded })}
        >
          {isSidebarExpanded && (
            <div className='overflow-hidden'>
              <AnimatePresence>
                <motion.h2
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: 1,
                    transition: {
                      duration: 0.1,
                    },
                  }}
                  exit={{ opacity: 0 }}
                  className='whitespace-nowrap text-lg font-medium text-primary'
                >
                  {sites.length} {sites.length === 1 ? 'site' : 'sites'}
                </motion.h2>
              </AnimatePresence>
            </div>
          )}{' '}
          <motion.div
            className='flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border border-grey-20 bg-white hover:bg-primary hover:bg-opacity-10 active:bg-primary active:bg-opacity-5 active:shadow-sm'
            onClick={handleSidebarToggle}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <FontAwesomeIcon
              icon={isSidebarExpanded ? faChevronLeft : faChevronRight}
              className='h-3 w-3 text-grey-50'
            />
          </motion.div>
        </div>

        {/* Site List */}
        <div
          onMouseEnter={onSidebarMouseEnter}
          onMouseLeave={onSidebarMouseLeave}
          className={classNames(
            'scrollbar-hide flex-1 overflow-auto',
            variant === 'calendar' && 'pt-2'
          )}
          ref={listRef}
        >
          {sites.length === 0 ? (
            <div className='flex h-full w-full flex-col items-center justify-start p-8 pt-20 text-center'>
              <div className='mb-4 rounded-full bg-grey-5 p-6'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='text-grey-40'
                >
                  <circle cx='11' cy='11' r='8'></circle>
                  <path d='m21 21-4.3-4.3'></path>
                </svg>
              </div>
              <h3 className='mb-2 text-lg font-medium'>
                No places match your filters
              </h3>
              <p className='max-w-md text-grey-40'>
                There are no places that match your filters. Try adjusting your
                filter criteria or clearing some filters.
              </p>
            </div>
          ) : (
            <div
              style={{
                height: sidebarVirtualizer.getTotalSize(),
                position: 'relative',
                width: '100%',
              }}
            >
              {virtualItems.map((virtualItem) => {
                const site = sites[virtualItem.index];

                if (!site) return null;

                return (
                  <div
                    key={String(virtualItem.key)}
                    className='absolute left-0 top-0 w-full'
                    style={{
                      transform: `translateY(${virtualItem.start}px)`,
                      height: virtualItem.size,
                    }}
                  >
                    <SiteCard
                      site={site}
                      height={
                        viewType === 'calendar' ? 70 : siteHeight(site.id)
                      }
                      isSidebarExpanded={isSidebarExpanded}
                      expandedRow={isSiteExpanded(site.id)}
                      onToggleExpandedRow={onToggleSiteExpansion}
                      isCurrentSite={site.id === currentSiteId}
                      dimmed={
                        viewType === 'calendar' &&
                        !!currentSiteId &&
                        site.id !== currentSiteId
                      }
                      navigationPath={navigationPath}
                      variant={variant === 'schedule' ? 'schedule' : 'sidebar'}
                      currentSiteId={currentSiteId}
                      contextVariant={variant}
                      onSiteNavigate={onSiteNavigate}
                      collapsedInitials={
                        initialsBySiteId[site.id] ??
                        computeInitialParts(site.name).base
                      }
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
});

CalendarSidebar.displayName = 'CalendarSidebar';

export { SiteCard };
export default CalendarSidebar;
